import api from 'services/api';
import { array, bool, enforce, nullable, object, runSchema, str, type FieldParser } from 'validation';

/**
 * One period during which the window stands open.
 *
 * Both ends are **offset-bearing** ISO strings (`toISOString(true)`), so they are
 * unambiguous instants that also record the zone offset they were rendered in.
 * Don't reduce them to a wall clock and back: a repeated hour at a DST fall-back
 * maps to two instants and the round trip silently picks the earlier one.
 */
export interface OpenPeriod {
  readonly open: string;
  readonly close: string;
}

export const openPeriodSchema: FieldParser<OpenPeriod> = object<OpenPeriod>({
  open: str(),
  close: str(),
});

/**
 * What evaluating a window says about it.
 *
 * `schedule_open_in_given_period` holds the openings the evaluator found within
 * its horizon — the whole point of the call. `is_open` answers for the requested
 * instant. `next_open_period` is only ever populated from the evaluator's cache
 * and is `null` on every freshly computed path; it is declared because an endpoint
 * contract states the response the backend actually sends, and narrowing to what
 * one consumer reads is an operation's job.
 */
export interface SchedulingWindowEvaluation {
  readonly schedule_open_in_given_period: readonly OpenPeriod[];
  readonly next_open_period: OpenPeriod | null;
  readonly is_open: boolean;
}

const evaluationSchema = object<SchedulingWindowEvaluation>({
  schedule_open_in_given_period: array(openPeriodSchema),
  next_open_period: nullable(openPeriodSchema),
  is_open: bool(),
});

export interface EvaluateSchedulingWindowRequest {
  readonly zoneName: string;
  readonly windowName: string;
  /** The instant to evaluate at, as an ISO string. The backend defaults to now. */
  readonly timestamp: string;
  /**
   * The zone the evaluation runs in. The backend falls back to a hardcoded
   * `Europe/Amsterdam` when this is absent, so always send it.
   */
  readonly timezone: string;
}

/**
 * POST `/scheduling_windows/:zone_name/:window_name/evaluate`.
 *
 * A read dressed as a POST — it computes nothing persistent, the body just
 * carries the instant and zone to evaluate against. Throws on HTTP error (404
 * for an unknown window) or structural validation failure; a period missing an
 * end is dropped, since it cannot be rendered as an interval.
 *
 * Note the periods come back rendered in the *window's* zone for a schedule that
 * inherits its timezone, not necessarily the zone asked for (nap-backend #1375).
 * The instants are correct — do not "fix" this by re-deriving zones client-side.
 */
export const evaluateSchedulingWindow = async (request: EvaluateSchedulingWindowRequest): Promise<SchedulingWindowEvaluation> => {
  const { zoneName, windowName, timestamp, timezone } = request;
  const res = await api.post(`/scheduling_windows/${encodeURIComponent(zoneName)}/${encodeURIComponent(windowName)}/evaluate`, {
    timestamp,
    timezone,
  });
  const r = runSchema(evaluationSchema, res.data, 'evaluateSchedulingWindow');
  if (!r.ok) {
    throw new Error('evaluateSchedulingWindow: response failed validation');
  }
  return enforce(r.value, r.warnings, ['schedule_open_in_given_period[*].open', 'schedule_open_in_given_period[*].close']);
};
